import React from 'react';
import { XCircleIcon } from 'lucide-react';
export function ProblemSection() {
  return <section className="w-full bg-white" aria-label="El problema" data-id="element-159">
      <div className="max-w-3xl mx-auto px-5 py-16 md:py-24" data-id="element-160">
        {/* Section Headline */}
        <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A4A] text-center leading-tight mb-4" data-id="element-161">
          Esto no es una guía "bonita".
        </h2>
        <p className="text-xl md:text-2xl text-center text-[#C41E3A] font-semibold mb-12" data-id="element-162">
          Es una guía para cuando ya no puedes.
        </p>

        {/* Situation List */}
        <div className="bg-gray-50 rounded-2xl p-6 md:p-10 mb-12" data-id="element-163">
          <p className="text-lg md:text-xl text-gray-700 mb-6 font-medium" data-id="element-164">
            Si estás leyendo esto, probablemente estás en una de estas
            situaciones:
          </p>
          <ul className="space-y-4" role="list" data-id="element-165">
            <li className="flex items-start gap-3" data-id="element-166">
              <span className="text-[#C41E3A] mt-1 flex-shrink-0" data-id="element-167">●</span>
              <span className="text-lg text-gray-800" data-id="element-168">
                El dolor te sube en la noche y{' '}
                <strong data-id="element-169">te deja sin dormir.</strong>
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-170">
              <span className="text-[#C41E3A] mt-1 flex-shrink-0" data-id="element-171">●</span>
              <span className="text-lg text-gray-800" data-id="element-172">
                Sientes latidos en la muela y te da miedo que{' '}
                <strong data-id="element-173">"sea infección".</strong>
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-174">
              <span className="text-[#C41E3A] mt-1 flex-shrink-0" data-id="element-175">●</span>
              <span className="text-lg text-gray-800" data-id="element-176">
                No sabes si ir a{' '}
                <strong data-id="element-177">urgent care, ER o esperar al dentista.</strong>
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-178">
              <span className="text-[#C41E3A] mt-1 flex-shrink-0" data-id="element-179">●</span>
              <span className="text-lg text-gray-800" data-id="element-180">
                Te da vergüenza porque <strong data-id="element-181">"debiste ir antes"</strong>… pero
                ahora lo único que importa es hoy.
              </span>
            </li>
          </ul>
        </div>

        {/* Truth Statement */}
        <div className="text-center mb-12" data-id="element-182">
          <p className="text-xl md:text-2xl text-[#1B2A4A] font-bold leading-relaxed" data-id="element-183">
            Y aquí va la verdad, sin azúcar:
          </p>
          <p className="text-lg md:text-xl text-gray-700 mt-4 max-w-2xl mx-auto leading-relaxed" data-id="element-184">
            El dolor dental es de los pocos dolores que te rompen la calma en
            minutos. Te vuelve pequeña. Te pone irritable. Te asusta. Y cuando
            pides ayuda, muchas veces te dicen:{' '}
            <span className="text-[#C41E3A] font-semibold italic" data-id="element-185">
              "la próxima semana".
            </span>
          </p>
        </div>

        {/* Bridge */}
        <div className="bg-[#1B2A4A] text-white rounded-2xl p-6 md:p-10 mb-12" data-id="element-186">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-6" data-id="element-187">
            El problema no es que no tengas remedios.
          </h3>
          <p className="text-xl md:text-2xl text-center text-[#F59E0B] font-bold" data-id="element-188">
            El problema es que no tienes un plan.
          </p>
        </div>

        {/* Failed Pattern */}
        <div className="space-y-1 mb-8" data-id="element-189">
          <p className="text-lg text-gray-700 font-medium mb-4" data-id="element-190">
            La mayoría hace esto:
          </p>
          <div className="space-y-3" data-id="element-191">
            <div className="flex items-start gap-3" data-id="element-192">
              <XCircleIcon className="w-6 h-6 text-[#EF4444] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-193" />
              <p className="text-lg text-gray-700" data-id="element-194">
                Prueba algo al azar (clavo, gel, enjuague, té)
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-195">
              <XCircleIcon className="w-6 h-6 text-[#EF4444] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-196" />
              <p className="text-lg text-gray-700" data-id="element-197">Se alivia un rato</p>
            </div>
            <div className="flex items-start gap-3" data-id="element-198">
              <XCircleIcon className="w-6 h-6 text-[#EF4444] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-199" />
              <p className="text-lg text-gray-700" data-id="element-200">Vuelve peor</p>
            </div>
            <div className="flex items-start gap-3" data-id="element-201">
              <XCircleIcon className="w-6 h-6 text-[#EF4444] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-202" />
              <p className="text-lg text-gray-700" data-id="element-203">
                Empieza la ansiedad:{' '}
                <em data-id="element-204">"¿me voy a dormir? ¿y si amanezco peor?"</em>
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-205">
              <XCircleIcon className="w-6 h-6 text-[#EF4444] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-206" />
              <p className="text-lg text-gray-700" data-id="element-207">
                Llama a una clínica, no hay cita
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-208">
              <XCircleIcon className="w-6 h-6 text-[#EF4444] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-209" />
              <p className="text-lg text-gray-700" data-id="element-210">
                Se rinde y aguanta… <strong data-id="element-211">hasta que revienta</strong>
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-xl md:text-2xl text-[#1B2A4A] font-bold" data-id="element-212">
          No es falta de fuerza. Es falta de estructura.
        </p>
      </div>
    </section>;
}